import { useEffect } from 'react';

import {
  ListTeamsUsersQuery,
  ProjectMemberRole,
  useListTeamsUsersQuery,
} from '@graphql/generated';
import { useGetTeamAvatarsByIdsQuery } from '@hooks/useGetTeamAvatarsByIdsQuery';

export type TeamWithUsers = ListTeamsUsersQuery['listTeams'][number];

interface ListTeamsUsersFromQueryProps {
  role?: ProjectMemberRole;
  includePending?: boolean;
  skip?: boolean;
}

/**
 * TA-9872: team member lists were split out of listTeams into the dedicated
 * listTeamsUsers query so only member-picker surfaces pay for them.
 */
export const useListTeamsUsersFromQuery = (
  props?: ListTeamsUsersFromQueryProps
) => {
  const {
    data: { listTeams } = {},
    loading,
    error,
    refetch,
  } = useListTeamsUsersQuery({
    variables: {
      role: props?.role,
      includePending: props?.includePending || false,
    },
    fetchPolicy: 'cache-and-network',
    skip: props?.skip,
  });

  const teams: TeamWithUsers[] = listTeams || [];
  const teamsWithoutPersonal = teams.filter((t) => !t.personal);

  // Batch-prefetch team avatar thumbnails (mirrors useListTeamsFromQuery) so
  // CachedImage doesn't lazy-fetch each team avatar individually (N+1).
  const { checkTeamAvatarURLByIds } = useGetTeamAvatarsByIdsQuery();
  useEffect(() => {
    const avatarIds = new Set<string>();
    teams.forEach((t) => {
      if (t.avatarFile?.id) {
        avatarIds.add(t.avatarFile.id);
      }
    });
    checkTeamAvatarURLByIds([...avatarIds]);
  }, [teams.length]);

  return { teams, teamsWithoutPersonal, loading, error, refetch };
};
